import { MfeReactComponent } from '@carvana/microfrontend-react';
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '|/components/base/theme';
import Close from '|/icons/close.svg';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    -webkit-transform: translateX(100%);
  }
  to {
    transform: translateX(0);
    -webkit-transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    -webkit-transform: translateX(0);
  }
  to {
    transform: translateX(100%);
    -webkit-transform: translateX(100%);
  }
`;

const HeaderContainer = styled.div`
  position: relative;
  width: 100%;
  background: white;
  padding: 12px 0;
  box-shadow: 0 2px 3px rgba(24 53 88 / 10%);
  z-index: 1500;
`;

const Title = styled.h5`
  text-transform: uppercase;
  margin: 0 auto;
  line-height: 20px;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const CloseButtonContainer = styled.div`
  width: 20px;
  height: 20px;
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;
  right: 11px;
`;

const CloseButton = styled(Close)`
  height: 14px;
  width: 14px;
`;

const SupportDropdownContainer = styled.div`
  position: relative;

  --transition-speed: 0.4s;

  max-height: 90%;
  padding-bottom: 40px;
  -webkit-scrollbar-width: none;
  -moz-scrollbar-width: none;
  -ms-scrollbar-width: none;
  scrollbar-width: none;
  max-width: 100vw;
  margin: 16px 8px;
  box-sizing: border-box;
  overflow: hidden auto;
`;

const SupportDropdownWrapper = styled.div<{
  isOpen: boolean;
  hasOpened: boolean;
}>`
  flex-grow: 1;
  height: 100%;
  z-index: 1500;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  background-color: white;
  width: 100vw;
  
  @media (width > ${theme.breakpoints.sm}) {
    width: 375px;
  }
  
  border-radius: 3px;
  transition: transform 0.3s ease-out;
  display: ${props => (props.hasOpened ? 'block' : 'none')};

  @media (min-width: ${theme.breakpoints.sm}) {
    animation: ${props =>
      props.hasOpened ? (props.isOpen ? slideIn : slideOut) : 'none'}
      0.3s forwards;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0 0 0 / 50%);
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 1499;
`;

const MfeDivContainer = styled.div`
  z-index: 9999;
`;

const SupportDropdownCloseEvent = 'header|support-dropdown|close';

const defaultProps = {
  isOpen: true,
  closeAction: () => {},
  closeMobileNavMenu: () => {}
};

export const SupportMFE = ({
  isOpen,
  closeAction,
  closeMobileNavMenu
} = defaultProps) => {
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
    }
  }, [isOpen]);

  const eventElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let eventElemRef = null;
    const handler = (event: Event) => {
      event.preventDefault();
      if (closeMobileNavMenu) {
        closeMobileNavMenu();
      }
      closeAction();
    };
    if (eventElement.current) {
      eventElemRef = eventElement.current;
      eventElement.current.addEventListener(SupportDropdownCloseEvent, handler);
    }
    return () => {
      if (eventElemRef) {
        eventElemRef.removeEventListener(SupportDropdownCloseEvent, handler);
      }
    };
  }, [eventElement, closeAction, closeMobileNavMenu]);

  return isOpen ? (
    <MfeDivContainer id="mfeContainer">
      <Overlay
        onClick={closeAction}
        isOpen={isOpen}
        data-analytics-id="pp_support_contact_overlay"
      />
      <SupportDropdownWrapper isOpen={isOpen} hasOpened={hasOpened}>
        <HeaderContainer>
          <Title>Support & Contact</Title>
          <CloseButtonContainer>
            <CloseButton
              onClick={closeAction}
              data-analytics-id="pp_support_contact_close"
            />
          </CloseButtonContainer>
        </HeaderContainer>
        <SupportDropdownContainer>
          <div id="account-menu-scroll-target" />
          <div ref={eventElement} id="header-plugin-events" />
          <MfeReactComponent
            module="CoreSupportDropdown"
            component="default"
            onComponentLoaded={() => {}}
            onComponentError={() => {}}
            withModal={false}
            isOpen={isOpen}
          />
        </SupportDropdownContainer>
      </SupportDropdownWrapper>
    </MfeDivContainer>
  ) : null;
};
